import { DateRangeTypes, FieldMapping } from '@business/entities/dynamicReprots';
import { CommonService } from '@business/services';
import { FormInput, FormSelect } from '@core/components/form';
import { FormDateRangeExtra } from '@core/components/form/FormDateRangeExtra';
import { formControl, FormDataType, formGroup } from '@core/utils/form';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { ReportBuilderFormData } from '@modules/dynamicReports/ReportForms';
import React, { memo, useMemo } from 'react';
import { Col, Row } from 'reactstrap';

export function getDateRangeValue(defaultRange: DateRangeTypes) {
  switch (defaultRange) {
    case DateRangeTypes.today:
      return `${toMoment().startOf('day').unix()}-${toMoment().endOf('day').unix()}`;
    case DateRangeTypes.yesterday:
      return `${toMoment().subtract(1, 'day').startOf('day').unix()}-${toMoment().subtract(1, 'day').endOf('day').unix()}`;
    case DateRangeTypes.last7:
      return `${toMoment().subtract(7, 'day').unix()}-${toMoment().unix()}`;
    case DateRangeTypes.last30:
      return `${toMoment().subtract(30, 'day').unix()}-${toMoment().unix()}`;
    case DateRangeTypes.month:
      return `${toMoment().startOf('month').unix()}-${toMoment().endOf('month').unix()}`;
    case DateRangeTypes.lastMonth:
      return `${toMoment().subtract(1, 'month').startOf('month').unix()}-${toMoment().subtract(1, 'month').endOf('month').unix()}`;
    case DateRangeTypes.year:
      return `${toMoment().startOf('year').unix()}-${toMoment().endOf('year').unix()}`;
    case DateRangeTypes.lastYear:
      return `${toMoment().subtract(1, 'year').startOf('year').unix()}-${toMoment().subtract(1, 'year').endOf('year').unix()}`;
  }
}

export function defineReportViewForm(filters: FieldMapping[], defaultRange: DateRangeTypes, dateRangeValue?: string) {
  return formGroup({
    filters: formGroup({
      ...R.fromPairs(filters.map(field => [`${field.key}`, formControl({ label: field.name })])),
    }),
    dateRange: formControl<string>({
      label: 'Date Range',
      initialValue: dateRangeValue || getDateRangeValue(defaultRange),
    }),
    search: formControl<string>({ label: 'Search', initialValue: '' }),
  });
}

export type ReportFilterFormType = ReturnType<typeof defineReportViewForm>;
export type ReportFilterFormData = FormDataType<typeof defineReportViewForm>;
export interface ReportFiltersProps {
  report: ReportBuilderFormData;
  form: ReportFilterFormType;
  builder?: boolean;
}

export const ReportFilters = memo((props: ReportFiltersProps) => {
  const { report, form, builder = false } = props;

  const filtersContent = useMemo(() => {
    return report.reportFilters.map((filter, index) => {
      const mapping = report.source.mapping[filter];

      let content: JSX.Element | undefined;
      const control = form.controls.filters.controls[filter];
      if (R.keys(mapping.filterItems).length > 0) {
        content = (
          <FormSelect type="multiple" control={control} items={CommonService.selectItems(R.keys(mapping.filterItems), i => mapping.filterItems[i])} />
        );
      } else {
        content = <FormInput control={control} />;
      }

      return (
        <Col lg={builder ? 4 : 2} key={index}>
          {content}
        </Col>
      );
    });
  }, [builder, form.controls.filters.controls, report.reportFilters, report.source.mapping]);

  return (
    <Row>
      {!!report.dateColumn && (
        <Col lg={builder ? 4 : 3}>
          <FormDateRangeExtra control={form.controls.dateRange} />
        </Col>
      )}
      {!!report.search && (
        <Col lg={builder ? 4 : 3}>
          <FormInput control={form.controls.search} />
        </Col>
      )}
      {filtersContent}
    </Row>
  );
});
